import { useCallback, useEffect, useRef, useState } from "react";
import {
    DraftContextMeshApiError,
    draftContextMeshProgress,
    getDraftContextMeshAnalysis,
    scheduleDraftContextMeshAnalysis,
    type DraftCanonicalContextMeshAnalysis,
    type DraftContextMeshResponse,
    type DraftContextMeshAnalysisStage,
    type DraftContextMeshStatus
} from "./draftContextMeshApi";

const ANALYSIS_DEBOUNCE_MS = 4_000;
const FAST_POLL_INTERVAL_MS = 2_000;
const SLOW_POLL_INTERVAL_MS = 10_000;
const FAST_POLL_ATTEMPTS = 15;
const RECOVERY_POLL_MAX_INTERVAL_MS = 10_000;

export type DraftContextMeshUiStatus = "unavailable" | "debouncing" | DraftContextMeshStatus;

export interface DraftContextMeshData {
    analysis: DraftCanonicalContextMeshAnalysis | null;
    analyzedDraftVersion: number | null;
    status: DraftContextMeshUiStatus;
    isStale: boolean;
    error: DraftContextMeshApiError | null;
    analysisStage?: DraftContextMeshAnalysisStage;
    analysisMessage?: string;
    retry: () => void;
}

interface PersistedRevision {
    draftId: number;
    draftVersion: number;
}

interface UseDraftContextMeshOptions {
    enabled: boolean;
    token: string | null | undefined;
    draftId: number | null;
    revisionKey: string;
    baselineReady: boolean;
    ensurePersisted: (
        revisionKey: string,
        forceSave?: boolean,
        staleDraftVersion?: number
    ) => Promise<PersistedRevision>;
    getAnalysis?: (token: string, draftId: number) => Promise<DraftContextMeshResponse>;
    scheduleAnalysis?: (token: string, draftId: number, draftVersion: number) => Promise<DraftContextMeshResponse>;
}

function shouldPoll(response: DraftContextMeshResponse): boolean {
    return response.status === "pending"
        || response.status === "sources_pending"
        || (response.status === "failed" && response.reason === "analysis_worker_unavailable");
}

function isTerminalResponse(response: DraftContextMeshResponse): boolean {
    return response.status === "failed" && !shouldPoll(response);
}

function isRecoverableError(reason: unknown): boolean {
    if (!(reason instanceof DraftContextMeshApiError)) return true;
    return [408, 425, 429].includes(reason.status) || reason.status >= 500;
}

function asApiError(reason: unknown): DraftContextMeshApiError {
    return reason instanceof DraftContextMeshApiError
        ? reason
        : new DraftContextMeshApiError(
            503,
            "ANALYSIS_UNAVAILABLE",
            reason instanceof Error ? reason.message : "Context analysis is temporarily unavailable."
        );
}

export function useDraftContextMeshAnalysis({
    enabled,
    token,
    draftId,
    revisionKey,
    baselineReady,
    ensurePersisted,
    getAnalysis = getDraftContextMeshAnalysis,
    scheduleAnalysis = scheduleDraftContextMeshAnalysis
}: UseDraftContextMeshOptions): DraftContextMeshData {
    const [analysis, setAnalysis] = useState<DraftCanonicalContextMeshAnalysis | null>(null);
    const [analyzedDraftVersion, setAnalyzedDraftVersion] = useState<number | null>(null);
    const [status, setStatus] = useState<DraftContextMeshUiStatus>(baselineReady ? "debouncing" : "baseline_incomplete");
    const [isStale, setIsStale] = useState(false);
    const [error, setError] = useState<DraftContextMeshApiError | null>(null);
    const [progress, setProgress] = useState<ReturnType<typeof draftContextMeshProgress>>({});
    const [retryAttempt, setRetryAttempt] = useState(0);
    const [terminalRestoreDraftId, setTerminalRestoreDraftId] = useState<number | null>(null);
    const [terminalAnalysisRevision, setTerminalAnalysisRevision] = useState<string | null>(null);
    const retryImmediatelyRef = useRef(false);
    const displayedVersionRef = useRef<number | null>(null);
    const currentDraftVersionRef = useRef(-1);
    const currentReadyVersionRef = useRef<number | null>(null);
    const currentReadyGenerationRef = useRef<number | null>(null);
    const analysisRef = useRef<DraftCanonicalContextMeshAnalysis | null>(null);
    const requestGenerationRef = useRef(0);
    const restoreGenerationRef = useRef(0);
    const activeDraftIdRef = useRef<number | null>(draftId);
    const terminalRestoreDraftIdRef = useRef<number | null>(null);
    const terminalAnalysisRevisionRef = useRef<string | null>(null);

    useEffect(() => {
        // A terminal result belongs to one analysis-input revision. Editing
        // those inputs creates a new revision that should schedule normally.
        terminalAnalysisRevisionRef.current = null;
        setTerminalAnalysisRevision(null);
        setProgress({});
    }, [revisionKey]);

    const applyResponse = useCallback((response: DraftContextMeshResponse, responseGeneration: number) => {
        if (response.draftId !== activeDraftIdRef.current) return;
        if (response.currentDraftVersion < currentDraftVersionRef.current) return;
        const isExactCurrent = response.status === "ready"
            && response.resultSource === "current_ready"
            && response.analyzedDraftVersion === response.currentDraftVersion;
        const isActiveConfigRefresh = response.resultSource === "last_ready"
            && response.isStale
            && response.analyzedDraftVersion === response.currentDraftVersion;
        if (
            response.currentDraftVersion === currentDraftVersionRef.current &&
            currentReadyVersionRef.current === response.currentDraftVersion &&
            !isExactCurrent &&
            (!isActiveConfigRefresh || currentReadyGenerationRef.current === responseGeneration)
        ) return;
        currentDraftVersionRef.current = response.currentDraftVersion;
        if (isExactCurrent) {
            currentReadyVersionRef.current = response.currentDraftVersion;
            currentReadyGenerationRef.current = responseGeneration;
        }
        if (response.analysis && response.analyzedDraftVersion !== null) {
            const displayed = displayedVersionRef.current;
            if (displayed === null || response.analyzedDraftVersion >= displayed) {
                // The API parser admits an analysis only when the overall score and
                // all four canonical category scores form one valid bundle.
                displayedVersionRef.current = response.analyzedDraftVersion;
                analysisRef.current = response.analysis;
                setAnalysis(response.analysis);
                setAnalyzedDraftVersion(response.analyzedDraftVersion);
            }
        }
        setStatus(response.status);
        setProgress(draftContextMeshProgress(response));
        setIsStale(response.resultSource === "last_ready" || response.isStale);
        setError(response.status === "failed"
            ? response.reason === "external_context_missing"
                ? new DraftContextMeshApiError(
                    409,
                    "EXTERNAL_CONTEXT_MISSING",
                    "The saved Jira or Azure DevOps import is no longer available. Reconnect that integration to continue."
                )
                : response.reason === "analysis_worker_unavailable"
                    ? new DraftContextMeshApiError(
                        503,
                        "ANALYSIS_WORKER_UNAVAILABLE",
                        "Context analysis is waiting for a healthy worker and will resume automatically."
                    )
                    : response.reason === "source_processing_timeout"
                        ? new DraftContextMeshApiError(
                            504,
                            "SOURCE_PROCESSING_TIMEOUT",
                            "Source processing did not finish in time. Retry the failed source or analysis."
                        )
                        : response.reason === "analysis_timeout"
                            ? new DraftContextMeshApiError(
                                504,
                                "ANALYSIS_TIMEOUT",
                                "Context analysis timed out. Retry the analysis."
                            )
                            : new DraftContextMeshApiError(503, "ANALYSIS_FAILED", "Context analysis failed. Retry when you're ready.")
            : null);
    }, []);

    // Restore the last canonical result when a persisted draft is resumed.
    useEffect(() => {
        const restoredRevisionKey = revisionKey;
        const previousDraftId = activeDraftIdRef.current;
        activeDraftIdRef.current = draftId;
        const generation = ++restoreGenerationRef.current;
        if (previousDraftId !== draftId) {
            setProgress({});
            currentDraftVersionRef.current = -1;
            currentReadyVersionRef.current = null;
            currentReadyGenerationRef.current = null;
            displayedVersionRef.current = null;
            analysisRef.current = null;
            setAnalysis(null);
            setAnalyzedDraftVersion(null);
            setIsStale(false);
            terminalRestoreDraftIdRef.current = null;
            setTerminalRestoreDraftId(null);
            terminalAnalysisRevisionRef.current = null;
            setTerminalAnalysisRevision(null);
        }
        if (!enabled || !token || draftId == null) {
            setProgress({});
            displayedVersionRef.current = null;
            analysisRef.current = null;
            setAnalysis(null);
            setAnalyzedDraftVersion(null);
            setIsStale(false);
            setError(null);
            setStatus("unavailable");
            terminalRestoreDraftIdRef.current = null;
            setTerminalRestoreDraftId(null);
            terminalAnalysisRevisionRef.current = null;
            setTerminalAnalysisRevision(null);
            return;
        }
        getAnalysis(token, draftId)
            .then(response => {
                if (restoreGenerationRef.current !== generation) return;
                if (terminalRestoreDraftIdRef.current === draftId) {
                    terminalRestoreDraftIdRef.current = null;
                }
                setTerminalRestoreDraftId(current => current === draftId ? null : current);
                applyResponse(response, -generation);
                if (isTerminalResponse(response)) {
                    const terminalKey = `${draftId}:${restoredRevisionKey}`;
                    terminalAnalysisRevisionRef.current = terminalKey;
                    setTerminalAnalysisRevision(terminalKey);
                }
                // A resumed draft may have an analysis request in flight even
                // though its current required fields are incomplete. Preserve
                // any prior completed result, but never present that request as
                // active analysis until the baseline is valid again.
                if (!baselineReady) {
                    setStatus("baseline_incomplete");
                    setIsStale(response.analysis !== null || analysisRef.current !== null);
                    setError(null);
                }
            })
            .catch(reason => {
                if (restoreGenerationRef.current !== generation) return;
                // The scheduling effect below owns recovery. A transient
                // restore failure must not strand a resumed draft in an error
                // state before it can reconcile its durable job.
                if (isRecoverableError(reason)) {
                    return;
                }
                terminalRestoreDraftIdRef.current = draftId;
                setTerminalRestoreDraftId(draftId);
                setStatus("failed");
                setIsStale(analysisRef.current !== null);
                setError(asApiError(reason));
            });
    }, [applyResponse, baselineReady, draftId, enabled, getAnalysis, token]);

    useEffect(() => {
        if (!enabled) return;
        if (!baselineReady) {
            setStatus("baseline_incomplete");
            setIsStale(analysisRef.current !== null);
            return;
        }
        if (!token) {
            setStatus("unavailable");
            return;
        }
        if (draftId != null && terminalRestoreDraftId === draftId) return;
        // A first-time draft can still have a null prop-level ID immediately
        // after persistence. Match the revision suffix as well as the durable
        // ID so a terminal response cannot restart this effect in a misleading
        // "Analyzing" state before the parent receives that ID.
        if (terminalAnalysisRevision?.endsWith(`:${revisionKey}`)) return;
        const analysisToken = token;

        let cancelled = false;
        let pollTimer: ReturnType<typeof setTimeout> | null = null;
        let pollAttempts = 0;
        let recoveryAttempts = 0;
        const generation = ++requestGenerationRef.current;
        setStatus("debouncing");
        setIsStale(analysisRef.current !== null);
        setError(null);

        const accept = (response: DraftContextMeshResponse) => {
            if (cancelled || requestGenerationRef.current !== generation) return false;
            applyResponse(response, generation);
            if (isTerminalResponse(response)) {
                const terminalKey = `${response.draftId}:${revisionKey}`;
                terminalAnalysisRevisionRef.current = terminalKey;
                setTerminalAnalysisRevision(terminalKey);
            }
            return true;
        };

        const markRecovering = () => {
            setStatus("pending");
            setIsStale(analysisRef.current !== null);
            setError(null);
        };

        const scheduleNextPoll = (
            targetDraftId: number,
            targetVersion: number,
            recovering = false
        ): void => {
            const interval = recovering
                ? Math.min(
                    FAST_POLL_INTERVAL_MS * (2 ** Math.min(recoveryAttempts, 3)),
                    RECOVERY_POLL_MAX_INTERVAL_MS
                )
                : pollAttempts < FAST_POLL_ATTEMPTS
                    ? FAST_POLL_INTERVAL_MS
                    : SLOW_POLL_INTERVAL_MS;
            if (recovering) recoveryAttempts += 1;
            else pollAttempts += 1;
            pollTimer = setTimeout(() => poll(targetDraftId, targetVersion), interval);
        };

        const failTerminally = (reason: unknown) => {
            if (cancelled || requestGenerationRef.current !== generation) return;
            setStatus("failed");
            setIsStale(analysisRef.current !== null);
            setError(asApiError(reason));
        };

        function poll(targetDraftId: number, targetVersion: number): void {
            getAnalysis(analysisToken, targetDraftId)
                .then(async response => {
                    if (!accept(response)) return;

                    let latest = response;
                    if (shouldPoll(latest) && latest.job == null) {
                        try {
                            // An autosave can advance the persisted draft while
                            // an older exact-version score is running. A GET for
                            // that newer version then has no durable job to poll.
                            // Heal that orchestration gap here rather than
                            // leaving the UI in "Analyzing" forever.
                            latest = await scheduleAnalysis(
                                analysisToken,
                                targetDraftId,
                                latest.currentDraftVersion
                            );
                            if (!accept(latest)) return;
                        } catch (reason) {
                            if (
                                reason instanceof DraftContextMeshApiError &&
                                reason.status === 409 &&
                                reason.code === "DRAFT_VERSION_STALE"
                            ) {
                                // The draft advanced again between the GET and
                                // POST. Keep converging on the newest version.
                                scheduleNextPoll(targetDraftId, latest.currentDraftVersion);
                                return;
                            }
                            if (isRecoverableError(reason)) {
                                markRecovering();
                                scheduleNextPoll(targetDraftId, latest.currentDraftVersion, true);
                                return;
                            }
                            throw reason;
                        }
                    }

                    const currentReady = latest.status === "ready"
                        && latest.resultSource === "current_ready"
                        && latest.analyzedDraftVersion === latest.currentDraftVersion;
                    const reachedTargetVersion = latest.currentDraftVersion >= targetVersion;
                    if (currentReady && reachedTargetVersion) {
                        recoveryAttempts = 0;
                        return;
                    }
                    if (!reachedTargetVersion) {
                        markRecovering();
                        scheduleNextPoll(targetDraftId, targetVersion, true);
                        return;
                    }
                    if (latest.job != null) recoveryAttempts = 0;
                    if (shouldPoll(latest)) {
                        scheduleNextPoll(targetDraftId, latest.currentDraftVersion);
                    }
                })
                .catch(reason => {
                    if (cancelled || requestGenerationRef.current !== generation) return;
                    if (isRecoverableError(reason)) {
                        markRecovering();
                        scheduleNextPoll(targetDraftId, targetVersion, true);
                        return;
                    }
                    failTerminally(reason);
                });
        }

        const schedule = async () => {
            try {
                if (draftId != null && terminalRestoreDraftIdRef.current === draftId) return;
                // Once a fresh schedule begins, a slow restore request must not
                // overwrite its newer status.
                restoreGenerationRef.current += 1;
                let forcePersistence = false;
                let staleDraftVersion: number | undefined;
                while (!cancelled && requestGenerationRef.current === generation) {
                    const persisted = forcePersistence
                        ? await ensurePersisted(revisionKey, true, staleDraftVersion)
                        : await ensurePersisted(revisionKey);
                    forcePersistence = false;
                    staleDraftVersion = undefined;
                    if (cancelled || requestGenerationRef.current !== generation) return;
                    activeDraftIdRef.current = persisted.draftId;
                    if (terminalRestoreDraftIdRef.current === persisted.draftId) return;
                    if (terminalAnalysisRevisionRef.current === `${persisted.draftId}:${revisionKey}`) return;
                    let response: DraftContextMeshResponse;
                    try {
                        response = await scheduleAnalysis(analysisToken, persisted.draftId, persisted.draftVersion);
                    } catch (reason) {
                        if (
                            !(reason instanceof DraftContextMeshApiError) ||
                            reason.status !== 409 ||
                            reason.code !== "DRAFT_VERSION_STALE"
                        ) {
                            if (isRecoverableError(reason)) {
                                // The server may already own this job. Reconcile
                                // via GET before considering another POST.
                                markRecovering();
                                scheduleNextPoll(persisted.draftId, persisted.draftVersion, true);
                                return;
                            }
                            throw reason;
                        }
                        // Autosaves can advance the draft repeatedly while the
                        // request is in flight. Re-persist the newest snapshot
                        // and keep converging without a tight retry loop.
                        forcePersistence = true;
                        staleDraftVersion = persisted.draftVersion;
                        await new Promise(resolve => setTimeout(resolve, 100));
                        continue;
                    }
                    if (!accept(response)) return;
                    if (shouldPoll(response)) scheduleNextPoll(persisted.draftId, persisted.draftVersion);
                    return;
                }
            } catch (reason) {
                if (cancelled || requestGenerationRef.current !== generation) return;
                failTerminally(reason);
            }
        };

        const delay = retryImmediatelyRef.current ? 0 : ANALYSIS_DEBOUNCE_MS;
        retryImmediatelyRef.current = false;
        const debounceTimer = setTimeout(() => void schedule(), delay);
        return () => {
            cancelled = true;
            clearTimeout(debounceTimer);
            if (pollTimer) clearTimeout(pollTimer);
        };
    }, [
        applyResponse,
        baselineReady,
        draftId,
        enabled,
        ensurePersisted,
        getAnalysis,
        terminalAnalysisRevision,
        terminalRestoreDraftId,
        retryAttempt,
        revisionKey,
        scheduleAnalysis,
        token
    ]);

    const retry = useCallback(() => {
        terminalRestoreDraftIdRef.current = null;
        setTerminalRestoreDraftId(null);
        terminalAnalysisRevisionRef.current = null;
        setTerminalAnalysisRevision(null);
        retryImmediatelyRef.current = true;
        setRetryAttempt(value => value + 1);
    }, []);

    const visibleProgress = status === "pending" || status === "sources_pending" ? progress : {};
    return { analysis, analyzedDraftVersion, status, isStale, error, retry, ...visibleProgress };
}
